import React, { ReactNode, useEffect, useMemo, useState } from "react";
import type { User } from "firebase/auth";
import { Button, Card, Form, Nav, Spinner } from "react-bootstrap";
import {
  Navigate,
  NavLink,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import "./App.css";
import { useTheme } from "./contexts/ThemeContext";
import usePlatformAuth from "./hooks/usePlatformAuth";
import LoginCard from "./components/auth/LoginCard";
import UnauthorizedNotice from "./components/auth/UnauthorizedNotice";
import { signInWithEmailAndPassword, signOut } from "./api/auth";
import LegalEditorPage from "./components/legal/LegalEditorPage";
import { LegacyBridgeSettingsPage } from "./components/legacyBridge";
import ExchangeSourcesPage from "./components/exchangeSources/ExchangeSourcesPage";
import VesPaymentsPage from "./components/vesPayments/VesPaymentsPage";
import PaymentMethodsPage from "./components/paymentMethods/PaymentMethodsPage";
import PlanRemindersPage from "./components/planReminders/PlanRemindersPage";
import OrganizationsPage from "./components/organizations";
import { OverviewPage } from "./components/overview";
import PlatformUsersPage from "./components/platformUsers";

interface RouteDefinition {
  id: string;
  label: string;
  path: string;
  element: ReactNode;
}

const getRoutes = (user: User): RouteDefinition[] => [
  {
    id: "overview",
    label: "Overview",
    path: "/overview",
    element: <OverviewPage user={user} />,
  },
  {
    id: "organizations",
    label: "Organizations",
    path: "/organizations",
    element: <OrganizationsPage user={user} />,
  },
  {
    id: "platform-users",
    label: "Platform users",
    path: "/platform-users",
    element: <PlatformUsersPage user={user} />,
  },
  {
    id: "ves-payments",
    label: "VES payments",
    path: "/ves-payments",
    element: <VesPaymentsPage user={user} />,
  },
  {
    id: "payment-methods",
    label: "Payment methods",
    path: "/payment-methods",
    element: <PaymentMethodsPage user={user} />,
  },
  {
    id: "plan-reminders",
    label: "Plan reminders",
    path: "/plan-reminders",
    element: <PlanRemindersPage user={user} />,
  },
  {
    id: "exchange-sources",
    label: "Exchange sources",
    path: "/exchange-sources",
    element: <ExchangeSourcesPage user={user} />,
  },
  {
    id: "legal",
    label: "Legal",
    path: "/legal",
    element: <LegalEditorPage user={user} />,
  },
  {
    id: "legacy-bridge",
    label: "Legacy bridge",
    path: "/legacy-bridge",
    element: <LegacyBridgeSettingsPage user={user} />,
  },
];

const PlatformShell: React.FC<{ routes: RouteDefinition[] }> = ({ routes }) => {
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  const activeNavLabel = useMemo(() => {
    const match = routes.find((route) =>
      location.pathname.startsWith(route.path),
    );
    return match?.label ?? "Control Panel";
  }, [location.pathname, routes]);

  const shellClassName = navOpen ? "app-shell app-shell--nav-open" : "app-shell";

  return (
    <div className={`${shellClassName} bg-body`}>
      <div className="app-layout">
        <header className="mobile-topbar">
          <button
            type="button"
            className="mobile-topbar__menu"
            aria-expanded={navOpen}
            aria-controls="platform-sidebar"
            onClick={() => setNavOpen((open) => !open)}
          >
            <span className="mobile-topbar__menu-icon" aria-hidden="true" />
            <span className="visually-hidden">
              {navOpen ? "Close menu" : "Open menu"}
            </span>
          </button>
          <p className="mobile-topbar__title mb-0">{activeNavLabel}</p>
        </header>

        <button
          type="button"
          className="sidebar-backdrop"
          aria-hidden={!navOpen}
          tabIndex={navOpen ? 0 : -1}
          onClick={() => setNavOpen(false)}
        />

        <aside id="platform-sidebar" className="sidebar">
          <div className="sidebar__top">
            <div className="sidebar__brand">
              <div className="sidebar__brand-eyebrow">MayorX</div>
              <h2 className="sidebar__brand-title">Platform panel</h2>
            </div>
            <Button
              variant="link"
              className="sidebar__close d-lg-none"
              aria-label="Close menu"
              onClick={() => setNavOpen(false)}
            >
              ×
            </Button>
          </div>
          <Nav className="flex-column sidebar__nav">
            {routes.map((route) => (
              <Nav.Item key={route.id}>
                <NavLink
                  to={route.path}
                  end
                  className={({ isActive }) =>
                    `sidebar__link nav-link${isActive ? " active" : ""}`
                  }
                  onClick={() => setNavOpen(false)}
                >
                  {route.label}
                </NavLink>
              </Nav.Item>
            ))}
          </Nav>
          <div className="sidebar__footer">
            <Form.Check
              type="switch"
              id="platform-theme-switch"
              label="Dark mode"
              checked={theme === "dark"}
              onChange={toggleTheme}
              className="mb-1"
            />
            <div className="sidebar__footer-note">
              Store automation is paused. Builds stay internal until ops
              promotes them.
            </div>
            <Button
              variant="outline-danger"
              size="sm"
              className="w-100"
              onClick={signOut}
            >
              Log out
            </Button>
          </div>
        </aside>

        <main className="content">
          <Routes>
            <Route path="/" element={<Navigate to="/overview" replace />} />
            {routes.map((route) => (
              <Route key={route.id} path={route.path} element={route.element} />
            ))}
            <Route path="*" element={<Navigate to="/overview" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const authState = usePlatformAuth();
  const [routes, setRoutes] = useState<RouteDefinition[]>([]);

  const [userAuthenticated, setUserAuthenticated] = useState(false);

  useEffect(() => {
    if (userAuthenticated === false) {
      if (authState.status === "authorized" && authState.user) {
        setRoutes(getRoutes(authState.user));
        setUserAuthenticated(true);
        console.log("User authenticated:", authState.user);
      }
    }
  }, [authState.status, authState.user?.uid]);

  if (authState.status === "loading") {
    return (
      <div className="auth-wrapper">
        <Spinner animation="border" role="status" />
        <p className="text-muted mt-3">Checking your access...</p>
      </div>
    );
  }

  if (authState.status === "signed-out") {
    return (
      <div className="auth-wrapper">
        <LoginCard
          onSubmit={(email, password) =>
            signInWithEmailAndPassword(email, password).then(() => undefined)
          }
        />
      </div>
    );
  }

  if (authState.status === "unauthorized") {
    return (
      <div className="auth-wrapper">
        <UnauthorizedNotice onSignOut={signOut} />
      </div>
    );
  }

  if (authState.status === "error") {
    return (
      <div className="auth-wrapper">
        <Card className="shadow-sm auth-card">
          <Card.Body>
            <Card.Title className="text-center mb-3">
              Authentication error
            </Card.Title>
            <Card.Text className="text-muted text-center mb-4">
              {authState.error}
            </Card.Text>
            <div className="d-flex justify-content-center">
              <Button variant="outline-secondary" onClick={signOut}>
                Try again
              </Button>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return <PlatformShell routes={routes} />;
};

export default App;
